import React, { useEffect, useState, useCallback } from "react";
import { Card, Row, Col, Form, ListGroup, Badge, Spinner, InputGroup } from "react-bootstrap";
import Header from "../layouts/Header";
import axiosInstance from "../services/axiosInterceptor";
import { useLoading } from "../contexts/LoadingContext";

const API = process.env.REACT_APP_API;

export default function AdminPermissions() {
  const { setLoading } = useLoading();
  const [teachers, setTeachers]       = useState([]);
  const [positions, setPositions]     = useState([]);
  const [selected, setSelected]       = useState(null);  // selected teacher with positions
  const [search, setSearch]           = useState('');
  const [loadingRight, setLoadingRight] = useState(false);
  const [saving, setSaving]           = useState(null);  // pos_id being toggled

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      axiosInstance.get(`${API}/admin/getTeachers`),
      axiosInstance.get(`${API}/admin/getPositions`),
    ]).then(([tRes, pRes]) => {
      setTeachers(tRes.data);
      setPositions(pRes.data);
    }).catch(() => {})
      .finally(() => setLoading(false));
  }, [setLoading]);

  useEffect(() => { load(); }, [load]);

  const selectTeacher = (t) => {
    setLoadingRight(true);
    axiosInstance.get(`${API}/admin/getTeacherWithPositions/${t.t_id}`)
      .then(res => setSelected(res.data))
      .catch(() => setSelected(null))
      .finally(() => setLoadingRight(false));
  };

  const hasPosition = (pos_id) =>
    selected?.positions?.some(p => p.pos_id === pos_id);

  const togglePosition = async (pos_id) => {
    if (!selected || saving) return;
    setSaving(pos_id);
    try {
      if (hasPosition(pos_id)) {
        await axiosInstance.post(`${API}/admin/removePosition`, { t_id: selected.t_id, pos_id });
      } else {
        await axiosInstance.post(`${API}/admin/assignPosition`, { t_id: selected.t_id, pos_id });
      }
      // Refresh selected teacher's positions
      const res = await axiosInstance.get(`${API}/admin/getTeacherWithPositions/${selected.t_id}`);
      setSelected(res.data);
    } catch {}
    setSaving(null);
  };

  const filtered = teachers.filter(t =>
    t.t_name?.includes(search) || t.t_idnumber?.includes(search)
  );

  return (
    <React.Fragment>
      <Header />
      <div className="main main-app p-3 p-lg-4">
      <h4 className="main-title mb-4">กำหนดสิทธิ์ผู้ใช้</h4>

      <Row className="g-3">
        {/* Left: teacher list */}
        <Col md={5} lg={4}>
          <Card className="card-one h-100">
            <Card.Header className="pb-2">
              <InputGroup size="sm">
                <InputGroup.Text><i className="ri-search-line"></i></InputGroup.Text>
                <Form.Control placeholder="ค้นหาชื่อ / รหัส" value={search} onChange={e => setSearch(e.target.value)} />
              </InputGroup>
            </Card.Header>
            <Card.Body className="p-0" style={{ maxHeight: 560, overflowY: 'auto' }}>
              <ListGroup variant="flush">
                {filtered.map(t => (
                  <ListGroup.Item
                    key={t.t_id}
                    action
                    active={selected?.t_id === t.t_id}
                    onClick={() => selectTeacher(t)}
                    className="d-flex justify-content-between align-items-center"
                  >
                    <div>
                      <div className="fw-semibold">{t.t_name}</div>
                      <small className="text-muted">{t.t_idnumber}</small>
                    </div>
                    {t.t_status !== 1 && <Badge bg="secondary" className="ms-2">ปิดใช้</Badge>}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </Card.Body>
          </Card>
        </Col>

        {/* Right: position checkboxes */}
        <Col md={7} lg={8}>
          <Card className="card-one h-100">
            {!selected ? (
              <Card.Body className="d-flex align-items-center justify-content-center text-secondary">
                <div className="text-center">
                  <i className="ri-arrow-left-line fs-4 d-block mb-2"></i>
                  เลือกบุคลากรจากรายการด้านซ้าย
                </div>
              </Card.Body>
            ) : loadingRight ? (
              <Card.Body className="d-flex align-items-center justify-content-center">
                <Spinner animation="border" size="sm" />
              </Card.Body>
            ) : (
              <>
                <Card.Header>
                  <div className="fw-semibold">{selected.t_name}</div>
                  <small className="text-muted">{selected.t_idnumber}</small>
                </Card.Header>
                <Card.Body>
                  <p className="text-secondary small mb-3">คลิกเพื่อเพิ่ม/ลบสิทธิ์</p>
                  <Row className="g-2">
                    {positions.map(p => {
                      const checked = hasPosition(p.pos_id);
                      const busy    = saving === p.pos_id;
                      return (
                        <Col xs={12} sm={6} key={p.pos_id}>
                          <div
                            className={`border rounded p-2 d-flex align-items-center gap-2 cursor-pointer ${checked ? 'border-primary bg-primary bg-opacity-10' : ''}`}
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                            onClick={() => togglePosition(p.pos_id)}
                          >
                            {busy
                              ? <Spinner animation="border" size="sm" />
                              : <Form.Check type="checkbox" checked={checked} readOnly className="mb-0" />
                            }
                            <div>
                              <span className="fw-semibold me-2 text-muted" style={{ fontSize: 11 }}>#{p.pos_id}</span>
                              <span>{p.pos_name}</span>
                            </div>
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                </Card.Body>
              </>
            )}
          </Card>
        </Col>
      </Row>
      </div>
    </React.Fragment>
  );
}
